/**
 * PlanLock — wraps feature dla planów które go nie mają (Etap 3, 2026-05-14).
 *
 * Usage:
 *   <PlanLock feature="investments_module" requiredPlan="pro">
 *     <InvestmentsList />
 *   </PlanLock>
 *
 * Jeśli plan ma feature → renderuje children bez zmian.
 * Jeśli nie ma → pokazuje lock overlay + CTA "Upgrade do Pro".
 *
 * Auto-fetch myPlan przy mount (cache w window.__maskil_my_plan).
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMyPlan } from '../../lib/api';


// Simple module-level cache so PlanLock components nie spamują API
let cachedPlan = null;
let cachedAt = 0;
const CACHE_TTL_MS = 60_000;  // 60s


async function fetchMyPlan(force = false) {
  const now = Date.now();
  if (!force && cachedPlan && (now - cachedAt < CACHE_TTL_MS)) {
    return cachedPlan;
  }
  try {
    const r = await getMyPlan();
    cachedPlan = r;
    cachedAt = now;
    return r;
  } catch (err) {
    return null;
  }
}


export function invalidateMyPlanCache() {
  cachedPlan = null;
  cachedAt = 0;
}


export default function PlanLock({
  feature,
  requiredPlan = 'pro',
  children,
  hideCompletely = false,  // if true, render nothing when locked (instead of overlay)
  inline = false,           // if true, render small inline lock badge instead of full overlay
  lang = 'pl',
}) {
  const [myPlan, setMyPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyPlan().then((p) => {
      setMyPlan(p);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="opacity-50">{children}</div>;
  }

  const hasFeature = myPlan?.plan?.features?.[feature] === true;
  if (hasFeature) {
    return <>{children}</>;
  }

  if (hideCompletely) return null;

  if (inline) {
    return <InlineLockBadge requiredPlan={requiredPlan} feature={feature} lang={lang} />;
  }

  return (
    <LockOverlay
      requiredPlan={requiredPlan}
      feature={feature}
      currentPlan={myPlan?.current_plan_id || 'starter'}
      grandfatherActive={myPlan?.grandfather_active}
      lang={lang}
    >
      {children}
    </LockOverlay>
  );
}


function InlineLockBadge({ requiredPlan, feature, lang }) {
  const navigate = useNavigate();
  return (
    <Badge
      variant="outline"
      className="bg-amber-500/10 text-amber-300 border-amber-500/30 gap-1 cursor-pointer hover:bg-amber-500/20"
      onClick={() => navigate('/plans')}
      title={lang === 'pl' ? `Funkcja dostępna w planie ${requiredPlan.toUpperCase()}` : `Available in ${requiredPlan.toUpperCase()}`}
    >
      <Lock className="w-2.5 h-2.5" />
      {requiredPlan.toUpperCase()}
    </Badge>
  );
}


function LockOverlay({ children, requiredPlan, feature, currentPlan, grandfatherActive, lang }) {
  const navigate = useNavigate();
  return (
    <div className="relative">
      {/* Blur the locked content behind */}
      <div className="opacity-30 pointer-events-none blur-sm select-none">
        {children}
      </div>
      {/* Overlay */}
      <Card className="absolute inset-0 bg-card/95 backdrop-blur border-amber-500/30 flex items-center justify-center">
        <CardContent className="text-center py-6 px-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/20 mb-3">
            <Lock className="w-6 h-6 text-amber-400" />
          </div>
          <h3 className="font-semibold text-base mb-1">
            {lang === 'pl' ? 'Funkcja zablokowana' : 'Feature locked'}
          </h3>
          <p className="text-sm text-muted-foreground mb-3 max-w-sm">
            {lang === 'pl'
              ? `Ta funkcja jest dostępna w planie ${requiredPlan.toUpperCase()}. Twój obecny plan: ${currentPlan.toUpperCase()}.`
              : `This feature is available in ${requiredPlan.toUpperCase()}. Your plan: ${currentPlan.toUpperCase()}.`}
          </p>
          {grandfatherActive && (
            <p className="text-xs text-emerald-300 mb-3 max-w-sm">
              {lang === 'pl'
                ? '🎁 Okres przejściowy aktywny do 13.06.2026 — wszystkie features dostępne w cenie Startera. Po tej dacie potrzebny upgrade.'
                : '🎁 Grandfather period until 13.06.2026 — all features available at Starter price.'}
            </p>
          )}
          <Button
            onClick={() => navigate('/plans')}
            className="gap-1 bg-amber-500 hover:bg-amber-600 text-amber-950"
            data-testid="plan-lock-upgrade-cta"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {lang === 'pl' ? `Zobacz plan ${requiredPlan.toUpperCase()}` : `View ${requiredPlan.toUpperCase()} plan`}
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


/**
 * PlanBadge — mały badge "PRO" / "STARTER" przy feature row w UI (np. sidebar)
 */
export function PlanBadge({ plan = 'pro', size = 'sm' }) {
  const colors = {
    starter:    'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
    pro:        'bg-primary/15 text-primary border-primary/30',
    enterprise: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  };
  return (
    <Badge variant="outline" className={`${colors[plan] || colors.pro} text-[10px] uppercase`}>
      {plan}
    </Badge>
  );
}
